import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

/* =========================================================
   OPTIONS
========================================================= */

const CATEGORY_OPTIONS = [
  "Ekran",
  "Bateri",
  "Kamera",
  "Karikim",
  "Butona",
  "Mikrofon",
  "Altaparlant",
  "Kapak",
  "Kabllo",
  "Konektor",
  "Pllakë",
  "Tjetër",
];

const DEVICE_OPTIONS = [
  "Telefon",
  "Laptop",
  "Tablet",
  "Smartwatch",
  "Kufje",
  "Konsolë",
  "Pajisje tjetër",
];

const DEFAULT_BRANDS = [
  "Apple",
  "Samsung",
  "Xiaomi",
  "Huawei",
  "Honor",
  "Oppo",
  "OnePlus",
  "Realme",
  "Google",
  "Motorola",
  "Nokia",
  "Lenovo",
  "HP",
  "Dell",
  "Asus",
  "Acer",
  "Sony",
  "JBL",
  "Trust",
  "Baseus",
  "Anker",
  "Logitech",
];

const initialFormState = {
  partName: "",
  category: "",
  device: "",
  brand: "",
  customBrand: "",
  model: "",
  customModel: "",
  name: "",
  purchase_price: "",
  price: "",
  bonus_share_percent: "",
  stock: "",
  description: "",
  image: null,
};

/* =========================================================
   COMPONENT
========================================================= */

const AddPartForm = forwardRef(
  (
    {
      onAdd,
      initialData,
      parts = [],
    },
    ref
  ) => {
    const fileInputRef = useRef(null);

    const [formData, setFormData] =
      useState(initialFormState);

    const [imagePreview, setImagePreview] =
      useState("");

    const [submitting, setSubmitting] =
      useState(false);

    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    /* =====================================================
       RESET
    ===================================================== */

    const resetForm = () => {
      setFormData(initialFormState);
      setImagePreview("");
      setError("");
      setFieldErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    useImperativeHandle(ref, () => ({
      resetForm,
    }));

    /* =====================================================
       PREFILL FOR EDIT
    ===================================================== */

    useEffect(() => {
      if (!initialData) {
        resetForm();
        return;
      }

      setFormData({
        partName:
          initialData.part_name ||
          initialData.name ||
          "",

        category:
          initialData.category || "",

        device:
          initialData.device || "",

        brand:
          initialData.brand || "",

        customBrand: "",

        model:
          initialData.model || "",

        customModel: "",

        name:
          initialData.name || "",

        purchase_price:
          initialData.purchase_price ?? "",

        price:
          initialData.price ?? "",

        bonus_share_percent:
          initialData.bonus_share_percent ?? "",

        stock:
          initialData.stock ?? "",

        description:
          initialData.description || "",

        image: null,
      });

      if (initialData.image) {
        const apiUrl =
          process.env.REACT_APP_API_URL ||
          "https://masterphone-backend-2.onrender.com";

        setImagePreview(
          initialData.image.startsWith("http")
            ? initialData.image
            : `${apiUrl}${initialData.image}`
        );
      }
    }, [initialData]);

    /* =====================================================
       BRAND OPTIONS
    ===================================================== */

    const brandOptions = useMemo(() => {
      const values = new Set(
        DEFAULT_BRANDS
      );

      parts.forEach((part) => {
        if (part.brand) {
          values.add(part.brand);
        }
      });

      return Array.from(values).sort();
    }, [parts]);

    /* =====================================================
       SELECTED BRAND / MODEL
    ===================================================== */

    const selectedBrand =
      formData.brand === "__custom__"
        ? formData.customBrand.trim()
        : formData.brand;

    const selectedModel =
      formData.model === "__custom__"
        ? formData.customModel.trim()
        : formData.model;

    /* =====================================================
       MODEL OPTIONS
    ===================================================== */

    const modelOptions = useMemo(() => {
      const values = new Set();

      parts.forEach((part) => {
        const sameBrand =
          part.brand === selectedBrand;

        const sameDevice =
          !formData.device ||
          part.device === formData.device;

        if (
          sameBrand &&
          sameDevice &&
          part.model
        ) {
          values.add(part.model);
        }
      });

      return Array.from(values).sort();
    }, [
      parts,
      selectedBrand,
      formData.device,
    ]);

    /* =====================================================
       AUTO NAME
    ===================================================== */

    useEffect(() => {
      const generatedName = [
        formData.partName,
        selectedBrand,
        selectedModel,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      setFormData((previous) => ({
        ...previous,
        name: generatedName,
      }));
    }, [
      formData.partName,
      selectedBrand,
      selectedModel,
    ]);

    /* =====================================================
       HANDLE CHANGE
    ===================================================== */

    const handleChange = (event) => {
      const {
        name,
        value,
        files,
      } = event.target;

      if (name === "image") {
        const file = files?.[0] || null;

        setFormData((previous) => ({
          ...previous,
          image: file,
        }));

        if (file) {
          setImagePreview(
            URL.createObjectURL(file)
          );
        }

        return;
      }

      setFormData((previous) => ({
        ...previous,
        [name]: value,
      }));

      setFieldErrors((previous) => {
        const next = { ...previous };
        delete next[name];
        if (name === "customBrand" || name === "brand") delete next.brand;
        if (name === "customModel" || name === "model") delete next.model;
        return next;
      });
      if (error) setError("");
    };

    /* =====================================================
       VALIDATION
    ===================================================== */

    const validateForm = () => {
      const errors = {};

      if (!formData.partName.trim()) errors.partName = "Vendos emrin e pjesës.";
      if (!formData.category) errors.category = "Zgjidh kategorinë.";
      if (!formData.device) errors.device = "Zgjidh pajisjen.";
      if (!selectedBrand) errors.brand = "Zgjidh ose shkruaj brand-in.";
      if (!selectedModel) errors.model = "Zgjidh ose shkruaj modelin.";

      const purchasePrice = Number(formData.purchase_price);
      if (formData.purchase_price === "" || !Number.isFinite(purchasePrice) || purchasePrice < 0)
        errors.purchase_price = "Vendos një çmim hyrjeje të vlefshëm.";

      const price = Number(formData.price);
      if (formData.price === "" || !Number.isFinite(price) || price < 0)
        errors.price = "Vendos një çmim shitjeje të vlefshëm.";

      const bonus = Number(formData.bonus_share_percent);
      if (formData.bonus_share_percent === "" || !Number.isFinite(bonus) || bonus < 0 || bonus > 100)
        errors.bonus_share_percent = "Bonusi duhet të jetë nga 0% deri në 100%.";

      const stock = Number(formData.stock);
      if (formData.stock === "" || !Number.isInteger(stock) || stock < 0)
        errors.stock = "Stoku duhet të jetë numër i plotë pozitiv.";

      if (!initialData && !(formData.image instanceof File))
        errors.image = "Zgjidh një fotografi për pjesën.";

      setFieldErrors(errors);
      return Object.keys(errors).length
        ? "Ka fusha të paplotësuara ose të pavlefshme. Kontrollo fushat me të kuqe."
        : "";
    };

    /* =====================================================
       SUBMIT
    ===================================================== */

    const handleSubmit = async (
      event
    ) => {
      event.preventDefault();

      const validationError =
        validateForm();

      if (validationError) {
        setError(validationError);
        return;
      }

      setError("");
      setSubmitting(true);

      const payload = new FormData();

      payload.append(
        "name",
        formData.name.trim()
      );

      payload.append(
        "category",
        formData.category
      );

      payload.append(
        "device",
        formData.device
      );

      payload.append(
        "brand",
        selectedBrand
      );

      payload.append(
        "model",
        selectedModel
      );

      payload.append(
        "purchase_price",
        String(
          Number(formData.purchase_price || 0)
        )
      );

      payload.append(
        "price",
        String(
          Number(formData.price || 0)
        )
      );

      payload.append(
        "bonus_share_percent",
        String(
          Number(formData.bonus_share_percent || 0)
        )
      );

      payload.append(
        "stock",
        String(
          Number(formData.stock || 0)
        )
      );

      payload.append(
        "description",
        formData.description.trim()
      );

      if (
        formData.image instanceof File
      ) {
        payload.append(
          "image",
          formData.image
        );
      }

      try {
        await onAdd?.(payload);
      } catch (submitError) {
        setError(
          submitError.message ||
            "Pjesa nuk mund të ruhej."
        );
      } finally {
        setSubmitting(false);
      }
    };

    const errorStyle = (field) =>
      fieldErrors[field]
        ? {
            borderColor: "#dc2626",
            backgroundColor: "#fef2f2",
            boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.10)",
          }
        : undefined;

    const FieldError = ({ field }) =>
      fieldErrors[field] ? (
        <p className="mt-1 text-xs font-semibold text-red-600">
          {fieldErrors[field]}
        </p>
      ) : null;

    /* =====================================================
       UI
    ===================================================== */

    return (
      <div className="rounded-2xl bg-white">
        <form
          onSubmit={handleSubmit}
          className="space-y-5 p-5"
        >
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          {/* PART NAME */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Lloji i pjesës *
            </label>

            <input
              type="text"
              name="partName"
              value={formData.partName}
              onChange={handleChange}
              placeholder="P.sh. Ekran, bateri, kamera..."
              style={errorStyle("partName")}
              className="
                w-full rounded-xl
                border border-gray-300
                px-3 py-3
                outline-none
                focus:border-blue-500
                focus:ring-4
                focus:ring-blue-100
              "
            />
            <FieldError field="partName" />
          </div>

          {/* CATEGORY + DEVICE */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Kategoria *
              </label>

              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={errorStyle("category")}
                className="
                  w-full rounded-xl
                  border border-gray-300
                  bg-white px-3 py-3
                  outline-none
                  focus:border-blue-500
                  focus:ring-4
                  focus:ring-blue-100
                "
              >
                <option value="">
                  Zgjidh kategorinë
                </option>

                {CATEGORY_OPTIONS.map(
                  (category) => (
                    <option
                      key={category}
                      value={category}
                    >
                      {category}
                    </option>
                  )
                )}
              </select>
              <FieldError field="category" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Pajisja *
              </label>

              <select
                name="device"
                value={formData.device}
                onChange={(event) => {
                  setFormData(
                    (previous) => ({
                      ...previous,
                      device:
                        event.target.value,
                      model: "",
                      customModel: "",
                    })
                  );
                }}
                style={errorStyle("device")}
                className="
                  w-full rounded-xl
                  border border-gray-300
                  bg-white px-3 py-3
                  outline-none
                  focus:border-blue-500
                  focus:ring-4
                  focus:ring-blue-100
                "
              >
                <option value="">
                  Zgjidh pajisjen
                </option>

                {DEVICE_OPTIONS.map(
                  (device) => (
                    <option
                      key={device}
                      value={device}
                    >
                      {device}
                    </option>
                  )
                )}
              </select>
              <FieldError field="device" />
            </div>
          </div>

          {/* BRAND + MODEL */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Brand *
              </label>

              <select
                name="brand"
                value={formData.brand}
                onChange={(event) => {
                  setFormData(
                    (previous) => ({
                      ...previous,
                      brand:
                        event.target.value,
                      model: "",
                      customModel: "",
                    })
                  );
                }}
                style={errorStyle("brand")}
                className="
                  w-full rounded-xl
                  border border-gray-300
                  bg-white px-3 py-3
                  outline-none
                  focus:border-blue-500
                  focus:ring-4
                  focus:ring-blue-100
                "
              >
                <option value="">
                  Zgjidh brand-in
                </option>

                {brandOptions.map(
                  (brand) => (
                    <option
                      key={brand}
                      value={brand}
                    >
                      {brand}
                    </option>
                  )
                )}

                <option value="__custom__">
                  + Brand tjetër
                </option>
              </select>
              <FieldError field="brand" />

              {formData.brand ===
                "__custom__" && (
                <input
                  type="text"
                  name="customBrand"
                  value={
                    formData.customBrand
                  }
                  onChange={handleChange}
                  placeholder="Shkruaj brand-in"
                  style={errorStyle("brand")}
                  className="
                    mt-2 w-full
                    rounded-xl
                    border border-gray-300
                    px-3 py-3
                    outline-none
                    focus:border-blue-500
                    focus:ring-4
                    focus:ring-blue-100
                  "
                />
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Modeli *
              </label>

              <select
                name="model"
                value={formData.model}
                onChange={handleChange}
                disabled={!selectedBrand}
                style={errorStyle("model")}
                className="
                  w-full rounded-xl
                  border border-gray-300
                  bg-white px-3 py-3
                  outline-none
                  disabled:bg-gray-100
                  focus:border-blue-500
                  focus:ring-4
                  focus:ring-blue-100
                "
              >
                <option value="">
                  Zgjidh modelin
                </option>

                {modelOptions.map(
                  (model) => (
                    <option
                      key={model}
                      value={model}
                    >
                      {model}
                    </option>
                  )
                )}

                <option value="__custom__">
                  + Model i ri
                </option>
              </select>
              <FieldError field="model" />

              {formData.model ===
                "__custom__" && (
                <input
                  type="text"
                  name="customModel"
                  value={
                    formData.customModel
                  }
                  onChange={handleChange}
                  placeholder="Shkruaj modelin"
                  style={errorStyle("model")}
                  className="
                    mt-2 w-full
                    rounded-xl
                    border border-gray-300
                    px-3 py-3
                    outline-none
                    focus:border-blue-500
                    focus:ring-4
                    focus:ring-blue-100
                  "
                />
              )}
            </div>
          </div>

          {/* GENERATED NAME */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Emri i pjesës
            </label>

            <input
              type="text"
              value={formData.name}
              readOnly
              placeholder="Krijohet automatikisht"
              className="
                w-full rounded-xl
                border border-blue-200
                bg-blue-50 px-3 py-3
                font-semibold text-blue-800
              "
            />

            <p className="mt-1 text-xs text-gray-500">
              Emri krijohet nga lloji i
              pjesës, brand-i dhe modeli.
            </p>
          </div>

          {/* FINANCIAL DATA + STOCK */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Çmimi i hyrjes *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="purchase_price"
                  min="0"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={handleChange}
                  style={errorStyle("purchase_price")}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 pr-14 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
                <FieldError field="purchase_price" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  ALL
                </span>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Çmimi i shitjes *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="price"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  style={errorStyle("price")}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 pr-14 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
                <FieldError field="price" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  ALL
                </span>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Bonus nga fitimi (%) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="bonus_share_percent"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.bonus_share_percent}
                  onChange={handleChange}
                  style={errorStyle("bonus_share_percent")}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 pr-10 outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                />
                <FieldError field="bonus_share_percent" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-violet-600">
                  %
                </span>
              </div>

              <p className="mt-1 text-xs text-gray-500">
                Fitimi: {Math.max(
                  0,
                  Number(formData.price || 0) -
                    Number(formData.purchase_price || 0)
                ).toLocaleString("sq-AL")} ALL
                {" · "}
                Bonus: {(
                  Math.max(
                    0,
                    Number(formData.price || 0) -
                      Number(formData.purchase_price || 0)
                  ) *
                  (Number(formData.bonus_share_percent || 0) / 100)
                ).toLocaleString("sq-AL")} ALL
              </p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Stoku *
              </label>
              <input
                type="number"
                name="stock"
                min="0"
                step="1"
                value={formData.stock}
                onChange={handleChange}
                style={errorStyle("stock")}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
                <FieldError field="stock" />
            </div>
          </div>

          {/* DESCRIPTION */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Përshkrimi
            </label>

            <textarea
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Përshkrimi i pjesës..."
              className="
                w-full resize-y
                rounded-xl
                border border-gray-300
                px-3 py-3
                outline-none
                focus:border-blue-500
                focus:ring-4
                focus:ring-blue-100
              "
            />
          </div>

          {/* IMAGE */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Fotografia
            </label>

            <input
              ref={fileInputRef}
              type="file"
              name="image"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleChange}
              style={errorStyle("image")}
              className="
                w-full rounded-xl
                border border-dashed
                border-gray-300
                bg-gray-50 p-3
              "
            />
                <FieldError field="image" />

            {imagePreview && (
              <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-3">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mx-auto h-40 max-w-full rounded-lg object-contain"
                />
              </div>
            )}
          </div>

          {/* SUBMIT */}

          <button
            type="submit"
            disabled={submitting}
            className="
              w-full rounded-xl
              bg-gradient-to-r
              from-blue-600
              to-indigo-700
              px-5 py-3
              font-bold text-white
              shadow-md transition
              hover:shadow-lg
              active:scale-[0.99]
              disabled:cursor-not-allowed
              disabled:opacity-60
            "
          >
            {submitting
              ? "Duke ruajtur..."
              : initialData
                ? "Ruaj ndryshimet"
                : "Shto pjesën"}
          </button>
        </form>
      </div>
    );
  }
);

export default AddPartForm;